import { Flex, Grid, Group, Stack, Text } from '@mantine/core';
import { utils, writeFile } from 'xlsx';
import React, { useEffect, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import '../../scss/vendors/bstyles.scss';
import Button from '../../components/elements/Button';
import { useMutation } from '@tanstack/react-query';
import { showNotification } from '../../utilities/mantineNotification';
import Card from '../../components/elements/Card';
import dayjs from 'dayjs';
import id from 'dayjs/locale/id';
import { dateFormat } from '../../utilities/dataFormat';
import * as Unicons from '@iconscout/react-unicons';
import ChartFilter from './ChartFilter';
import { useDisclosure } from '@mantine/hooks';
import ModalChartTraining from '../../components/fragments/ModalChart';
import BarChart from '../../components/elements/BarChartbyYear';
import LoaderChart from './LoaderChart';
import { HiChevronRight } from 'react-icons/hi';
import useLocalStorage from '../../hooks/useUserData';
import {
  downloadChartBudget,
  downloadChartProgramMonitoringSE,
  downloadChartRater,
  downloadChartTSI,
  getChartBudget,
  getChartProgramMonitoring,
  getChartRater,
  getChartTSI
} from '../../services/dashboardPrincipal';
import ModalDownload from './ModalDownload';
import ChartFilterRater from './ChartFilterRater'

ChartJS.register(
  ChartDataLabels,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export interface DataDownloadReportTraining {
  courseId: string;
  courseName: string;
  completionDate: string;
  enrolmentDate: string;
  enrolmentMethod: string;
  buName: null | string;
  subBuName: null | string;
  deptName: null | string;
  binusianId: null | string;
  name: null | string;
  buCode: null | string;
  subBuCode: null | string;
  deptCode: null | string;
  sectionCode: null | string;
  sectionName: null | string;
  subSectionCode: null | string;
  subSectionName: null | string;
}

export interface ChartTrainingProps {
  yearFrom: string;
  yearTo: string;
  title: string;
  businessUnit?: string;
  subBusinessUnit?: string;
  departement?: string;
  buCode?: null | string;
  subBuCode?: null | string;
  deptCode?: null | string;
}

const ChartRater: React.FC<ChartTrainingProps> = ({
  title,
  yearFrom,
  yearTo,
  businessUnit,
  subBusinessUnit,
  departement,
  buCode,
  subBuCode,
  deptCode
}) => {
  const [opened, { open, close }] = useDisclosure(false);
  const [openedDownload, { toggle: toggleDownload }] = useDisclosure(false);
  const [chartModal, setChartModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [chartFilter, setChartFilter] = useState({
    yearFrom: null,
    yearTo: null,
    tahun: null,
    subBuName: null,
    deptName: null,
    sectionName: null,
    subBuCode: null,
    deptCode: null,
    sectionCode: null
  });
  const [chartLevel, setChartLevel] = useState(0);
  const [currentData, setCurrentData] = useState(null);
  const [chartConfig, setChartConfig] = useState([]);
  const [statusFMSTR, setStatusFMSTR] = useState('0');
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [userData, setUserData] = useLocalStorage('persist:root', null);
  const data = JSON.parse(userData?.auth);

  const fetchedDataChart = useMutation({
    mutationFn: () =>
      getChartRater({
        YearFrom: chartFilter.yearFrom,
        YearTo: chartFilter.yearTo,
        BinusianId: data?.userData?.binusianId,
        Tahun: chartFilter.tahun,
        SubBuName: chartFilter.subBuName,
        DeptName: chartFilter.deptName,
        SectionName: chartFilter.sectionName,
        SubBuCode: chartFilter.subBuCode,
        DeptCode: chartFilter.deptCode,
        SectionCode: chartFilter.sectionCode
      }),
    onError: (error: any) => {
      showNotification({
        type: 'error',
        message: error
      });
    }
  });

  const fetchedDownloadChart = useMutation({
    mutationFn: () =>
      downloadChartRater({
        YearFrom: chartFilter.yearFrom,
        YearTo: chartFilter.yearTo,
        BinusianId: data?.userData?.binusianId,
        Tahun: chartFilter.tahun,
        SubBuName: chartFilter.subBuName,
        SubBuCode: chartFilter.subBuCode,
        DeptCode: chartFilter.deptCode,
        SectionCode: chartFilter.sectionCode
      }),
    onSuccess: (data) => {
      if (data?.data?.data?.length > 0) {
        showNotification({
          type: 'success',
          message: 'Download in progress, please wait a moment.'
        });
        renderConvertExcel(data?.data?.data);
        return;
      }

      showNotification({
        type: 'error',
        message: `Data download is empty. Please try again later.`
      });
    },
    onError: (error: any) => {
      showNotification({
        type: 'error',
        message: error
      });
    }
  });

  const renderConvertExcel = (data: DataDownloadReportTraining[]) => {
    const formatFilename = () => {
      const parts = [`Chart Rater - download at ${dayjs().toLocaleString()}`];
      return parts.filter(Boolean).join('_');
    };

    const filename = formatFilename();
    const wb = utils.book_new();

    // Convert the JSON data to a worksheet
    const ws = utils.json_to_sheet(data);

    // Add the worksheet to the workbook
    utils.book_append_sheet(wb, ws, 'Sheet1');

    // Write the workbook to a file
    // This will trigger a file download in the browser
    writeFile(wb, `${filename}.xlsx`);
  };

  useEffect(() => {
    setChartFilter((prevState) => ({
      ...prevState,
      yearFrom,
      yearTo,
      subBuName: subBusinessUnit,
      departement,
      buCode,
      subBuCode,
      deptCode
    }));
  }, [
    yearTo,
    yearFrom,
    subBusinessUnit,
    departement,
    buCode,
    subBuCode,
    deptCode
  ]);

  useEffect(() => {
    if (chartFilter && chartFilter.yearFrom && chartFilter.yearTo) {
      const commonFields = {
        yearFrom: chartFilter.yearFrom,
        yearTo: chartFilter.yearTo,
        tahun: chartFilter.tahun,
        subBuName: chartFilter.subBuName,
        subBuCode: chartFilter.subBuCode
      };

      const levelConfig = [
        {},
        {},
        {
          subBuName: chartFilter.subBuName,
          subBuCode: chartFilter.subBuCode,
          deptName: chartFilter.deptName,
          deptCode: chartFilter.deptCode,
          sectionName: chartFilter.sectionName,
          sectionCode: chartFilter.sectionCode
        },
        {
          subBuName: chartFilter.subBuName,
          subBuCode: chartFilter.subBuCode,
          deptName: chartFilter.deptName,
          deptCode: chartFilter.deptCode,
          sectionName: chartFilter.sectionName,
          sectionCode: chartFilter.sectionCode
        },
        {
          subBuName: chartFilter.subBuName,
          subBuCode: chartFilter.subBuCode,
          deptName: chartFilter.deptName,
          deptCode: chartFilter.deptCode,
          sectionName: chartFilter.sectionName,
          sectionCode: chartFilter.sectionCode
        }
      ];

      if (
        chartLevel >= 1 &&
        chartLevel <= 5 &&
        chartConfig.length === chartLevel - 1
      ) {
        setChartConfig((prevState) => [
          ...prevState,
          { ...commonFields, ...levelConfig[chartLevel] }
        ]);
      }
    }
  }, [chartFilter, chartLevel, chartConfig.length]);

  useEffect(() => {
    fetchedDataChart.mutate();
  }, [
    chartFilter?.tahun,
    chartFilter?.subBuName,
    chartFilter?.yearFrom,
    chartFilter?.yearTo,
    chartFilter?.deptName,
    chartFilter?.sectionName
  ]);

  const onClick = (element) => {
    if (element.length > 0) {
      if (chartLevel === 0) {
        setChartModal(true);
      }
      if (chartModal) {
        const maxLevel = 4;
        if (chartLevel < maxLevel) {
          setChartLevel((prevLevel) => prevLevel + 1);
        }
        const index = element[0].index;
        // Get the label of the clicked bar
        const item = fetchedDataChart?.data?.data?.data[index];

        if (chartLevel < maxLevel) {
          setChartFilter((prevState) => ({
            ...prevState,
            tahun: item.year.toString(),
            subBuName: chartLevel === 1 ? item.label : prevState.subBuName,
            deptName: chartLevel === 2 ? item.label : prevState.deptName,
            sectionName: chartLevel === 3 ? item.label : prevState.sectionName,
            subBuCode: chartLevel === 1 ? item.code : prevState.subBuCode,
            deptCode: chartLevel === 2 ? item.code : prevState.deptCode,
            sectionCode: chartLevel === 3 ? item.code : prevState.sectionCode
          }));
        }
      }
    }
  };

  const handleCloseModal = () => {
    setChartFilter((prevState) => ({
      ...prevState,
      tahun: null,
      subBuName: null,
      deptName: null,
      sectionName: null,
      subBuCode: null,
      deptCode: null,
      sectionCode: null
    }));

    setLoading(true);

    setTimeout(() => {
      setChartLevel(0);
      setChartConfig([]);
      setChartModal(false);
      setLoading(false);
      fetchedDataChart.mutate();
    }, 500);
  };

  useEffect(() => {
    setChartFilter((prevState) => ({
      ...prevState,
      tahun: chartConfig[chartLevel - 1]?.tahun,
      subBuName: chartConfig[chartLevel - 1]?.subBuName,
      subBuCode: chartConfig[chartLevel - 1]?.subBuCode,
      deptCode: chartConfig[chartLevel - 1]?.deptCode,
      sectionCode: chartConfig[chartLevel - 1]?.sectionCode,
      deptName: chartConfig[chartLevel - 1]?.deptName,
      sectionName: chartConfig[chartLevel - 1]?.sectionName
    }));
    setCurrentData(chartConfig[chartLevel - 1]);
  }, [chartConfig, chartLevel]);

  const handleBack = () => {
    setLoading(true);
    if (chartLevel - 1 === 0) {
      setChartFilter((prevState) => ({
        ...prevState,
        tahun: null,
        subBuName: null,
        subBuCode: null,
        deptName: null,
        deptCode: null,
        sectionName: null,
        sectionCode: null
      }));
      fetchedDataChart.mutate();
    }
    setTimeout(() => {
      setChartLevel((prevState) => prevState - 1);
      setChartConfig((prevState) => prevState.slice(0, -1));
      setLoading(false);
    }, 500);
  };

  const handleDownload = () => fetchedDownloadChart.mutate();

  const renderContent = () => {
    return (
      <BarChart
        key={chartLevel.toString()}
        chartKeys={['expectation', 'raterScore']}
        chartLabels={['Expectation Score', 'Rater Score']}
        chartColors={['#7B2183', '#75B843']}
        data={fetchedDataChart?.data?.data?.data || []}
        onClick={onClick}
        loading={fetchedDataChart.isLoading}
        isError={fetchedDataChart.isError}
        onLoad={() => fetchedDataChart.mutate()}
        withLegend
      />
    );
  };

  const handleBreadcrumb = (item) => {
    setLoading(true);
    setTimeout(() => {
      setChartLevel(item.level);
      setChartConfig((prevState) => prevState.slice(0, item.level));
      setLoading(false);
    }, 500);
  };

  const renderModalBreadcrumb = () => {
    if (chartLevel <= 0) return null;

    const breadcrumbData = [
      {
        title: currentData?.year || '',
        level: 1
      },
      {
        title: currentData?.subBuName || '',
        level: 2
      },
      {
        title: currentData?.deptName || '',
        level: 3
      },
      {
        title: currentData?.sectionName || '',
        level: 4
      }
    ];

    const filteredItems = breadcrumbData.filter((item) => item.title);

    return (
      <Group gap={0}>
        {filteredItems.map((item, index) => (
          <React.Fragment key={index}>
            <Button
              onClick={() => handleBreadcrumb(item)}
              variant="transparent"
            >
              {item.title}
            </Button>
            {index !== filteredItems.length - 1 && <HiChevronRight />}
          </React.Fragment>
        ))}
      </Group>
    );
  };

  const onDownload = () => toggleDownload();

  const handleConfirmDownload = () => {
    setTimeout(() => {
      toggleDownload();
      handleDownload();
    }, 500);
  };

  const handleOpenFilterModal = () => setIsFilterModalOpen(true);
  const handleCloseFilterModal = () => setIsFilterModalOpen(false);

  const handleFilter = (filterData: any) => {
    setChartFilter((prevState) => ({
      ...prevState,
      ...filterData,
      tahun: null,
      subBuCode: null,
      deptCode: null,
      sectionCode: null,
      sectionName: null,
      deptName: null,
      subBuName: null
    }));
  };

  const handleResetLocalFilter = () => {
    setChartFilter((prevState) => ({
      ...prevState,
      yearFrom: yearFrom,
      yearTo: yearTo,
      bulan: null,
      tahun: null,
      subBuCode: null,
      deptCode: null,
      sectionCode: null,
      sectionName: null,
      deptName: null,
      subBuName: null
    }));
  };


  return (
    <>
      <Grid.Col span={{ base: 12, md: 4 }}>
        {fetchedDataChart.isLoading && <LoaderChart />}
        {!fetchedDataChart.isLoading && (
          <Card h={'100%'}>
            <Flex gap={16} justify={'space-between'}>
              <Stack gap={0}>
                <Text className="text-sm-semibold">{title}</Text>
                <Text className="text-xs-medium" c={'gray'}>
                  Period{' '}
                  {(chartFilter.yearFrom)}{' '}
                  -{' '}
                  {(chartFilter.yearTo)}
                </Text>
              </Stack>

              <Group>
                {fetchedDataChart.isError && (
                  <Button
                    variant="outline"
                    size="sm"
                    styles={{
                      padding: '6px 10px',
                      borderColor: '#CCCCCC',
                      color: '#333333',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 12
                    }}
                    onClick={() => fetchedDataChart.mutate()}
                  >
                    <Unicons.UilRefresh size={14} />
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  styles={{
                    padding: '6px 10px',
                    borderColor: '#CCCCCC',
                    color: '#333333',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12
                  }}
                  onClick={handleOpenFilterModal}
                >
                  <Unicons.UilFilter size={14} />
                </Button>
              </Group>
            </Flex>
            <Flex mt={16}>{renderContent()}</Flex>
          </Card>
        )}
      </Grid.Col>
      <ChartFilterRater
        isOpen={isFilterModalOpen}
        onClose={handleCloseFilterModal}
        onFilter={handleFilter}
        onReset={handleResetLocalFilter}
      />

      <ModalChartTraining
        title={title}
        description={renderModalBreadcrumb()}
        loading={loading}
        isVisible={chartModal}
        onClose={handleCloseModal}
        backButton={chartLevel > 0}
        onBack={handleBack}
        downloadable
        onDownload={onDownload}
        loadingDownload={fetchedDownloadChart.isLoading}
        chart={renderContent()}
      />

      <ModalDownload
        opened={openedDownload}
        title={`Download ${title}`}
        onClose={() => toggleDownload()}
        onConfirm={handleConfirmDownload}
      />
    </>
  );
};

export default ChartRater;
